import {updateManyPropertyNotes} from 'ducks/propertyNotes';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import {updatePropertyPreferences} from 'api/propertyPreferences';
import {
  addComparableItem,
  deleteComparableItemImage,
  featureItem,
  fetchComparableItems,
  getPage,
  moveSelectedItem,
  orderSelectedItem,
  removeFeatureItem,
  resetComparableItemFields,
  setDisplayItemField,
  updateComparableItemFetching,
  updateSelectAllItems,
  uploadComparableItemImage,
} from 'actionCreators/comparableItems';
import {getComparableRentals} from 'api/ComparablesApi';
import { COMPARABLE_TYPE } from '../constants/comparableTypes';

export const UPDATE = 'pulp/comparableRentals/UPDATE';
export const ADD_SELECTED = 'pulp/comparableRentals/ADD_SELECTED';
export const MOVE_SELECTED = 'pulp/comparableRentals/MOVE_SELECTED';
export const ORDER_SELECTED = 'pulp/comparableRentals/ORDER_SELECTED';
export const REMOVE_SELECTED = 'pulp/comparableRentals/REMOVE_SELECTED';
export const UPDATE_SORT_BY = 'pulp/comparableRentals/UPDATE_SORT_BY';
export const LOAD_MORE = 'pulp/comparableRentals/LOAD_MORE';
export const UPDATE_SUGGESTED = 'pulp/comparableRentals/UPDATE_SUGGESTED';
export const ADD_COMPARABLE = 'pulp/comparableRentals/ADD_COMPARABLE';
export const SELECT_ALL = 'pulp/comparableRentals/SELECT_ALL';
export const DESELECT_ALL = 'pulp/comparableRentals/DESELECT_ALL';
export const USE_SOI_CONSUMER_AFFAIRS_LAYOUT =
  'pulp/comparableRentals/USE_SOI_CONSUMER_AFFAIRS_LAYOUT';
export const UPDATE_ATTRIBUTE = 'pulp/comparableRentals/UPDATE_ATTRIBUTE';
export const FEATURE_PROPERTY = 'pulp/comparableRentals/FEATURE_PROPERTY';
export const REMOVE_FEATURE_PROPERTY = 'pulp/comparableRentals/REMOVE_FEATURE_PROPERTY';
export const SET_DISPLAY_FIELD = 'pulp/comparableRentals/SET_DISPLAY_FIELD';
export const RESET_COMPARABLE = 'pulp/comparableRentals/RESET_COMPARABLE';
export const UPDATE_ITEM_FETCHING = 'pulp/comparableRentals/UPDATE_ITEM_FETCHING';

export function updateForSession(payload, savePreference = false) {
  return (dispatch, getState) => {
    if (savePreference) {
      const {
        propertyDetail: {propertyId: targetPropertyId},
      } = getState();
      updatePropertyPreferences(targetPropertyId, {
        [`comparableRentals.properties.${payload.propertyId}:${JSON.stringify(
          payload.keyPath
        )}`]: payload,
      });
    }

    dispatch({type: UPDATE_ATTRIBUTE, payload});
  };
}

export function updateItems(payload) {
  return {type: UPDATE, payload};
}

export function addSelectedItem(propertyId) {
  return (dispatch, getState) => {
    const {
      propertyDetail: {propertyId: targetPropertyId},
      comparableRentals: {selected},
      comparableRentals: {featured},
    } = getState();

    updatePropertyPreferences(targetPropertyId, {
      'comparableRentals.selected': addComparableItem(propertyId, selected, featured),
    });

    dispatch({type: ADD_SELECTED, payload: propertyId});
  };
}

export function removeSelectedItem(propertyId) {
  return (dispatch, getState) => {
    const {
      propertyDetail: {propertyId: targetPropertyId},
      comparableRentals: {selected},
      reportType,
    } = getState();

    const newSelected = selected.filter((s) => s !== propertyId);
    updatePropertyPreferences(targetPropertyId, {
      'comparableRentals.selected': newSelected,
    });

    if (!newSelected.length) {
      updatePropertyPreferences(targetPropertyId, {
        'comparableRentals.selectedMoved': false,
      });
    }
    AnalyticsGateway.sendEvent({
      eventName: 'Remove Comparable',
      properties: [{label: 'Page', value: getPage(reportType, COMPARABLE_TYPE.RENTALS)}],
    });

    dispatch({type: REMOVE_SELECTED, payload: propertyId});
  };
}

export function updateSortBy(sortBy, savePreference = false) {
  return (dispatch, getState) => {
    if (savePreference) {
      const {
        propertyDetail: {propertyId},
      } = getState();
      updatePropertyPreferences(propertyId, {'comparableRentals.sortBy': sortBy});
    }

    dispatch({type: UPDATE_SORT_BY, payload: sortBy});
  };
}

export function loadMore() {
  return {type: LOAD_MORE};
}

export function addItem(data, featureFlags = {}) {
  return {type: ADD_COMPARABLE, payload: data, flags: featureFlags};
}

export function updateSuggestedItems() {
  return {type: UPDATE_SUGGESTED};
}

export function selectConsumerAffairs(useConsumerAffairsLayout) {
  return {type: USE_SOI_CONSUMER_AFFAIRS_LAYOUT, payload: useConsumerAffairsLayout};
}

export function fetchItem(propertyId, savePreference = false) {
  return async (dispatch, getState) => {
    const {
      propertyDetail: {propertyId: targetPropertyId, latitude, longitude},
      comparableRentals: {selected},
    } = getState();
    const {propertyNotes, ...data} = await getComparableRentals(targetPropertyId, propertyId, latitude, longitude);
    const {featureFlags} = getState();
    dispatch(addItem(data, featureFlags));
    dispatch(updateManyPropertyNotes({[data.propertyId]: propertyNotes}));

    if (savePreference) {
      updatePropertyPreferences(targetPropertyId, {
        'comparableRentals.selected': [propertyId, ...selected],
      });
    }
  };
}

export const featureProperty = (...args) => featureItem(COMPARABLE_TYPE.RENTALS)(...args);

export const removeFeatureProperty = (...args) =>
  removeFeatureItem(COMPARABLE_TYPE.RENTALS)(...args);

export const updateSelectAll = (...args) => updateSelectAllItems(COMPARABLE_TYPE.RENTALS)(...args);

export const moveSelected = (...args) => moveSelectedItem(COMPARABLE_TYPE.RENTALS)(...args);

export const orderSelected = (...args) => orderSelectedItem(COMPARABLE_TYPE.RENTALS)(...args);

export const fetchItems = (...args) => fetchComparableItems(COMPARABLE_TYPE.RENTALS)(...args);

export const uploadComparableImage = (...args) =>
  uploadComparableItemImage(COMPARABLE_TYPE.RENTALS)(...args);

export const deleteUploadedImage = (...args) =>
  deleteComparableItemImage(COMPARABLE_TYPE.RENTALS)(...args);

export const setDisplayField = (...args) => setDisplayItemField(COMPARABLE_TYPE.RENTALS)(...args);

export const resetComparableProperty = (...args) => resetComparableItemFields(COMPARABLE_TYPE.RENTALS)(...args);

export const updateFetching = (...args) => updateComparableItemFetching(COMPARABLE_TYPE.RENTALS)(...args);
